import { X } from 'lucide-react'
import { CONCEPT_CATEGORY_COLORS, STATUS_COLORS, type TrackerData, type MindMapData } from './mindmap-layout'

interface MindMapDetailPanelProps {
  nodeId: string | null
  tracker: TrackerData | null
  mindmap: MindMapData | null
  onClose: () => void
}

export function MindMapDetailPanel({ nodeId, tracker, mindmap, onClose }: MindMapDetailPanelProps) {
  if (!nodeId || !tracker) return null

  // Determine node type and render accordingly
  if (nodeId.startsWith('doc-')) {
    const docId = nodeId.replace('doc-', '')
    return <DocDetail docId={docId} tracker={tracker} mindmap={mindmap} onClose={onClose} />
  }

  if (nodeId.startsWith('concept-')) {
    const conceptId = nodeId.replace('concept-', '')
    return <ConceptDetail conceptId={conceptId} tracker={tracker} mindmap={mindmap} onClose={onClose} />
  }

  if (nodeId.startsWith('group-')) {
    const groupId = nodeId.replace('group-', '')
    return <GroupDetail groupId={groupId} tracker={tracker} mindmap={mindmap} onClose={onClose} />
  }

  return null
}

function PanelWrapper({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="absolute top-3 right-3 z-10 w-72 max-h-[calc(100%-1.5rem)] overflow-y-auto bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 sticky top-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-t-xl">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{title}</h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>
      <div className="px-4 py-3 space-y-3">
        {children}
      </div>
    </div>
  )
}

function DocDetail({
  docId,
  tracker,
  mindmap,
  onClose,
}: {
  docId: string
  tracker: TrackerData
  mindmap: MindMapData | null
  onClose: () => void
}) {
  const doc = tracker.documents.find((d) => d.id === docId)
  if (!doc) return null

  const concepts = mindmap?.documents[docId]?.concepts || []
  const incomingRels = tracker.interrelations.filter((r) => r.target === docId)
  const outgoingRels = tracker.interrelations.filter((r) => r.source === docId)
  const xrefsTo = mindmap?.crossReferences.filter((x) => x.targetDocId === docId) || []
  const xrefsFrom = mindmap?.crossReferences.filter((x) => x.sourceDocId === docId) || []

  return (
    <PanelWrapper title={doc.shortTitle} onClose={onClose}>
      <div className="text-xs text-gray-600 dark:text-gray-400">{doc.title}</div>
      <div className="flex items-center gap-2">
        <div
          className="w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: STATUS_COLORS[doc.status] || '#9ca3af' }}
        />
        <span className="text-xs text-gray-500 dark:text-gray-400">{doc.status}</span>
        <span className="text-xs text-gray-400 dark:text-gray-500">{doc.id}</span>
      </div>

      {doc.summary && (
        <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{doc.summary}</p>
      )}

      {concepts.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
            Concepts ({concepts.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {concepts.map((c) => (
              <span
                key={c.id}
                className="text-[10px] px-2 py-0.5 rounded-full border"
                style={{
                  borderColor: CONCEPT_CATEGORY_COLORS[c.category] || '#6b7280',
                  color: CONCEPT_CATEGORY_COLORS[c.category] || '#6b7280',
                }}
              >
                {c.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {(outgoingRels.length > 0 || incomingRels.length > 0) && (
        <div>
          <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
            Relationships ({outgoingRels.length + incomingRels.length})
          </div>
          <div className="space-y-1">
            {outgoingRels.map((r, i) => (
              <div key={`out-${i}`} className="text-[10px] text-gray-600 dark:text-gray-400">
                <span className="text-gray-400">-&gt;</span> {r.type} <span className="font-medium">{r.target}</span>
              </div>
            ))}
            {incomingRels.map((r, i) => (
              <div key={`in-${i}`} className="text-[10px] text-gray-600 dark:text-gray-400">
                <span className="text-gray-400">&lt;-</span> {r.type} <span className="font-medium">{r.source}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(xrefsTo.length > 0 || xrefsFrom.length > 0) && (
        <div>
          <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
            Cross-References ({xrefsTo.length + xrefsFrom.length})
          </div>
          <div className="space-y-1 text-[10px] text-gray-600 dark:text-gray-400">
            {xrefsFrom.slice(0, 8).map((x, i) => (
              <div key={`from-${i}`}>concept "{x.conceptId.split('::')[1]}" referenced in {x.targetDocId}</div>
            ))}
            {xrefsTo.slice(0, 8).map((x, i) => (
              <div key={`to-${i}`}>references concept from {x.sourceDocId}</div>
            ))}
            {(xrefsTo.length + xrefsFrom.length > 16) && (
              <div className="text-gray-400">...and {xrefsTo.length + xrefsFrom.length - 16} more</div>
            )}
          </div>
        </div>
      )}
    </PanelWrapper>
  )
}

function ConceptDetail({
  conceptId,
  tracker,
  mindmap,
  onClose,
}: {
  conceptId: string
  tracker: TrackerData
  mindmap: MindMapData | null
  onClose: () => void
}) {
  if (!mindmap) return null

  // Find the concept across all documents
  let concept: { id: string; label: string; category: string; description: string; importance: string; section: string } | null = null
  let sourceDocId = ''
  for (const [docId, docData] of Object.entries(mindmap.documents)) {
    const found = docData.concepts.find((c) => c.id === conceptId)
    if (found) {
      concept = found
      sourceDocId = docId
      break
    }
  }

  if (!concept) return null

  const sourceDoc = tracker.documents.find((d) => d.id === sourceDocId)
  const xrefs = mindmap.crossReferences.filter((x) => x.conceptId === conceptId)

  return (
    <PanelWrapper title={concept.label} onClose={onClose}>
      <div className="flex items-center gap-2">
        <div
          className="w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: CONCEPT_CATEGORY_COLORS[concept.category] || '#6b7280' }}
        />
        <span className="text-xs capitalize" style={{ color: CONCEPT_CATEGORY_COLORS[concept.category] || '#6b7280' }}>
          {concept.category}
        </span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500">
          {concept.importance}
        </span>
      </div>

      <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{concept.description}</p>

      <div>
        <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Source</div>
        <div className="text-xs text-gray-600 dark:text-gray-400">
          {sourceDoc?.shortTitle || sourceDocId} - {concept.section}
        </div>
      </div>

      {xrefs.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
            Referenced In ({xrefs.length} papers)
          </div>
          <div className="space-y-1">
            {xrefs.map((x, i) => {
              const targetDoc = tracker.documents.find((d) => d.id === x.targetDocId)
              return (
                <div key={i} className="text-[10px] text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                  <div
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: x.strength === 'direct' ? '#6366f1' : '#a5b4fc' }}
                  />
                  {targetDoc?.shortTitle || x.targetDocId}
                  <span className="text-gray-400">({x.strength})</span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </PanelWrapper>
  )
}

function GroupDetail({
  groupId,
  tracker,
  mindmap,
  onClose,
}: {
  groupId: string
  tracker: TrackerData
  mindmap: MindMapData | null
  onClose: () => void
}) {
  const docs = tracker.documents.filter((d) => (d.seriesGroup || 'standalone-wp') === groupId)
  const totalConcepts = docs.reduce((sum, d) => sum + (mindmap?.documents[d.id]?.concepts.length || 0), 0)
  const statusCounts = docs.reduce(
    (acc, d) => {
      acc[d.status] = (acc[d.status] || 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  return (
    <PanelWrapper title={groupId} onClose={onClose}>
      <div className="text-xs text-gray-600 dark:text-gray-400">
        {docs.length} documents, {totalConcepts} concepts
      </div>

      <div>
        <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Status</div>
        <div className="space-y-1">
          {Object.entries(statusCounts).map(([status, count]) => (
            <div key={status} className="flex items-center gap-1.5 text-[10px] text-gray-600 dark:text-gray-400">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: STATUS_COLORS[status] || '#9ca3af' }}
              />
              {status}: {count}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Documents</div>
        <div className="space-y-1">
          {docs.map((d) => (
            <div key={d.id} className="text-[10px] text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
              <div
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: STATUS_COLORS[d.status] || '#9ca3af' }}
              />
              <span className="font-medium">{d.id}</span> {d.shortTitle}
            </div>
          ))}
        </div>
      </div>
    </PanelWrapper>
  )
}
